import React from 'react';
import { Link } from 'react-router-dom';
import { ProductItem } from '../../services/product.service';
import { formatCurrency } from '../../utils/formatters';
import {
  DEFAULT_PRODUCT_IMAGE,
  getProductImageUrl,
  handleProductImageError,
} from '../../utils/productImage';

interface SearchResultsProps {
  results: ProductItem[];
  loading: boolean;
  onResultClick: () => void;
  searchQuery: string;
}

const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  loading,
  onResultClick,
  searchQuery,
}) => {
  if (loading) {
    return (
      <div className='absolute left-0 right-0 mt-2 bg-white shadow-lg rounded-lg border border-gray-200 py-2 max-h-96 z-50' 
           data-search-result>
        <div className='px-4 py-2 text-gray-500 text-sm'>
          <div className='flex items-center'>
            <svg
              className='animate-spin h-4 w-4 mr-2 text-primary'
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 24 24'
            >
              <circle
                className='opacity-25'
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='4'
              ></circle>
              <path
                className='opacity-75'
                fill='currentColor'
                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
              ></path>
            </svg>
            <span>Đang tìm kiếm...</span>
          </div>
        </div>
      </div>
    );
  }

  if (results.length === 0 && searchQuery.trim() !== '' && !loading) {
    return (
      <div className='absolute left-0 right-0 mt-2 bg-white shadow-lg rounded-lg border border-gray-200 py-2 z-50' 
           data-search-result>
        <div className='px-4 py-2 text-gray-500 text-sm'>
          Không tìm thấy sản phẩm phù hợp.
        </div>
      </div>
    );
  }

  if (results.length === 0 || searchQuery.trim() === '') {
    return null;
  }

  return (
    <div className='absolute left-0 right-0 mt-2 bg-white shadow-lg rounded-lg border border-gray-200 py-2 max-h-96 overflow-y-auto z-50' 
         data-search-result>
      <div className='px-4 py-2 text-xs text-gray-500 border-b border-gray-100'>
        {results.length} kết quả tìm kiếm cho "{searchQuery}"
      </div>
      {results.map((product) => (
        <Link
          key={product.id}
          to={`/products/${product.id}`}
          className='block p-3 hover:bg-gray-50 transition-colors duration-150 border-b border-gray-100 last:border-none'
          onClick={(e) => {
            // Delay clearing search results to prevent screen flash
            setTimeout(() => {
              onResultClick();
            }, 100);
          }}
        >
          <div className='flex items-center space-x-3'>
            <img
              src={getProductImageUrl(product.images?.[0])}
              alt={product.name}
              className='w-12 h-12 object-cover rounded-md border border-gray-200'
              onError={handleProductImageError}
            />
            <div className='flex-1 min-w-0'>
              <h4 className='text-sm font-medium text-gray-900 truncate'>
                {product.name}
              </h4>
              <div className='flex justify-between items-center mt-1'>
                <div className='flex items-center'>
                  <span className='text-primary font-medium text-sm'>
                    {formatCurrency(product.current_price)}
                  </span>
                  {product.is_on_sale && (
                    <span className='text-xs text-gray-500 line-through ml-2'>
                      {formatCurrency(product.original_price)}
                    </span>
                  )}
                </div>
                <span className='text-xs text-gray-500'>{product.unit}</span>
              </div>
            </div>
          </div>
        </Link>
      ))}
      {results.length > 5 && (
        <Link
          to={`/search?q=${encodeURIComponent(searchQuery)}`}
          className='block text-center py-2 text-primary text-sm font-medium hover:underline'
          onClick={onResultClick}
        >
          Xem tất cả kết quả
        </Link>
      )}
    </div>
  );
};

export default SearchResults;
